import axiosService from '../utils/axios';

/**
 * Interface for randomization configuration
 */
export interface RandomizationConfig {
  RANDOMIZE_DISCOVER_PAGE: boolean;
  RANDOMIZE_SEARCH_RESULTS: boolean;
  RANDOMIZE_SIMILAR_ARTISTS: boolean;
  RANDOMNESS_FACTOR_MIN: number;
  RANDOMNESS_FACTOR_MAX: number;
}

/**
 * Interface for the complete API configuration
 */
export interface APIConfig {
  // Include randomization settings
  RANDOMIZE_DISCOVER_PAGE: boolean;
  RANDOMIZE_SEARCH_RESULTS: boolean;
  RANDOMIZE_SIMILAR_ARTISTS: boolean;
  RANDOMNESS_FACTOR_MIN: number;
  RANDOMNESS_FACTOR_MAX: number;

  // Other config values
  NEW_USER_DAYS_THRESHOLD: number;
  SKILL_MATCH_POINTS: number;
  ARTIST_MATCH_POINTS: number;
  GENRE_MATCH_POINTS: number;
  EXACT_LOCATION_MATCH: number;
  HEADLINE_MATCH_POINTS: number;
  OPEN_TO_MATCH_POINTS: number;
  BOOST_BASE_POINTS: number;
  BOOST_MULTIPLIER: number;
  ALREADY_LIKED_PENALTY: number;
}

// Cache the config so we don't need to fetch it multiple times
const cachedConfig: APIConfig | null = null;

/**
 * Fetches the API configuration from the backend
 * @returns Promise<APIConfig> The API configuration object
 */
export const getAPIConfig = async (): Promise<APIConfig> => {
    // Return cached config if available
    if (cachedConfig) {
        return cachedConfig;
    }

    try {
        const { data } = await axiosService.get('/similarity/config');
        return data;
    } catch (error) {
        console.error('Error fetching API configuration:', error);
        // Return default values
        return {
            RANDOMIZE_DISCOVER_PAGE: true,
            RANDOMIZE_SEARCH_RESULTS: false,
            RANDOMIZE_SIMILAR_ARTISTS: false,
            RANDOMNESS_FACTOR_MIN: 0.0,
            RANDOMNESS_FACTOR_MAX: 1.5,
            NEW_USER_DAYS_THRESHOLD: 24,
            SKILL_MATCH_POINTS: 0.3,
            ARTIST_MATCH_POINTS: 0.8,
            GENRE_MATCH_POINTS: 0.5,
            EXACT_LOCATION_MATCH: 1.0,
            HEADLINE_MATCH_POINTS: 2.5,
            OPEN_TO_MATCH_POINTS: 1.25,
            BOOST_BASE_POINTS: 0.25,
            BOOST_MULTIPLIER: 0.25,
            ALREADY_LIKED_PENALTY: -0.35
        };
    }
};
